import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import countryList from "react-select-country-list";

import ConfirmationPopup from "../../packages/ConfirmationPopup";
import WPDropdown from "../../packages/WPDropdown";
import WPInput from "../../packages/WPInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import WPTextArea from "../../packages/WPTextArea";

import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";

import {
  fetchChemicalLibraryData,
  updateChemicalLibraryData,
} from "../../components/chemicalLibrary/ChemicalLibraryDialog/ChemicalLibraryDialog.api";
import { getInitialChemicalSelection } from "../../components/ChemicalSelectionFields/utils";
import ChemicalsUsedForCleaning from "../../components/ChemicalsUsedForCleaning";
import CommentsOnSpecialElements from "../../components/CommentsOnSpecialElements";
import Header from "../../components/Header";
import ImportPlantWavePro from "../../components/ImportPlantWavePro";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../components/Notifications";
import PageNavigation from "../../components/PageNavigation";
import { convertUtilityFormValues } from "../../components/UtilityCosts/utilityCosts.config";
import {
  toStoreUtilityCosts,
  toUtilityApiPayload,
  toUtilityFormValues,
} from "../../components/UtilityCosts/UtilityCostsDialog/UtilityCostsDialog.api";
import Settings from "../home/Settings";
import { MULTIPLE_WATER_TYPE_OPTION, WATER_TYPE } from "./addNewPlant.config";
import ImportTrainConfigModal from "./ImportTrainConfig";
import RoConfiguration from "./roConfiguration";

import {
  CustomModalButtons,
  PENDING_PLANT_BASELINE_UNITS_KEY,
  PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
  SESSION_STORAGE_KEYS,
} from "../../constants/global.constants";
import { API_RESPONSE_CODE } from "../../constants/apiResponseCode.constants";
import { UNITS } from "../../constants/units.constant";

import {
  DataAppendIcon,
  NewBadgeIcon,
  NodeExtensionIcon,
} from "../../assets/icons";

import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import {
  getItemSession,
  removeItemSession,
  saveItemSession,
  setLocalStorageItem,
} from "../../utils/session";
import {
  getChangedData,
  getSelectedPlantTrainInfo,
  updatePlantId,
} from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import {
  editPlant,
  getPlantDetails,
  getPlantUtilityCosts,
  savePlant,
  updatePlantChemicalLibrary,
  updatePlantUtilityCosts,
} from "../../network/cipAPI";
import {
  clearRONFElementsCache,
  fetchRONFElementsWithCache,
} from "../../network/ronfElementApiCache";

import {
  storePlantChemicalLibrary,
  storePlantDetails,
  storePlantUtilityCosts,
  storeUnits,
  storeUserChemicalLibrary,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";

const basePath = API_URLS.basePath;
const {
  NO_OF_TRAINS_KEY,
  PENDING_PLANT_UTILITY_COSTS_KEY,
  PENDING_USER_CHEMICALS_TO_BE_ADDED,
} = SESSION_STORAGE_KEYS;

const AddNewPlant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    token,
    flow,
    pressure,
    feed_quality,
    feed_water_factor,
    permeate_quality,
    feed_water_permeate_factor,
    temperature,
    volume,
    mass,
    currency,
    conversion_rate,
    plantData,
    accessUserPlant,
    plantChemicalLibrary,
    userChemicalLibrary,
    plantUtilityCosts,
  } = useSelector((state) => state);

  const [noOfTrains, setNoOfTrains] = useState(() =>
    String(getItemSession(NO_OF_TRAINS_KEY) || 1),
  );
  const [plantDetail, setPlantDetail] = useState([]);
  const [plantDetailerror, setPlantDetailError] = useState({});
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [createdPlantId, setCreatedPlantId] = useState(null);
  const [RONFElements, setRONFElements] = useState([]);
  const [chemicalSelection, setChemicalSelection] = useState({});
  const [isImportPlantModalOpen, setIsImportPlantModalOpen] = useState(false);
  const [showImportPlantConfirmation, setShowImportPlantConfirmation] =
    useState(false);
  const [showWaterTypeImpactWarning, setShowWaterTypeImpactWarning] =
    useState(false);
  const [pendingPlantWaterType, setPendingPlantWaterType] = useState("");
  const [isTrainConfigModalOpen, setIsTrainConfigModalOpen] = useState(false);
  const [roValidationContext, setRoValidationContext] = useState({
    show: false,
    invalidPassByTrain: {},
  });
  const [roReconcileTrigger, setRoReconcileTrigger] = useState(0);
  const [pendingWaveProUtilityCosts, setPendingWaveProUtilityCosts] = useState(
    {},
  );
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState({
    plantInformation: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    roConfiguration: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    settings: {
      fulfilled: true, // Always fulfilled - radio buttons always have valid values
      changed: false,
      error: false,
    },
    chemicalsUsedForCleaning: {
      fulfilled: true,
      changed: false,
      error: false,
    },
  });
  const [baselineUnits, setBaselineUnits] = useState(() => ({
    currency: currency || "USD",
    volume: volume || "m³",
    conversion_rate: Number(conversion_rate) || 1,
  }));
  const [pendingChemicalsToBeEdited, setPendingChemicalsToBeEdited] = useState(
    [],
  );

  const waterTypeOptions = useMemo(() => WATER_TYPE(t), [t]);
  const multipleWaterTypeOption = useMemo(
    () => MULTIPLE_WATER_TYPE_OPTION(t),
    [t],
  );

  // initialize chemical selection when plant chemical library is loaded & chemical selection is empty
  // or update chemical selection prices when plant chemical library changes
  useEffect(() => {
    if (!plantChemicalLibrary?.length) {
      return;
    }
    if (!chemicalSelection || Object.keys(chemicalSelection).length === 0) {
      const initialSelection =
        getInitialChemicalSelection(plantChemicalLibrary);
      setChemicalSelection(initialSelection);
    } else {
      const updatedSelection = { ...chemicalSelection };
      Object.keys(chemicalSelection).forEach((categoryKey) => {
        const chemId = chemicalSelection[categoryKey]?.chem_id;
        const plantChem = plantChemicalLibrary.find(
          (chem) => chem.chem_id === chemId,
        );
        if (plantChem) {
          updatedSelection[categoryKey] = {
            ...chemicalSelection[categoryKey],
            price: plantChem.price,
          };
        }
      });
      setChemicalSelection(updatedSelection);
    }
  }, [plantChemicalLibrary]);

  // In edit flow, fetch the user chemical library only if it is not already in the store (used by the import feature).
  // In add-plant flow, the user chemical library is fetched because no plant chemical library exists yet.
  useEffect(() => {
    const fetchUserChemicalLibrary = async () => {
      const userChemicalLibrary = await fetchChemicalLibraryData(t, true);
      dispatch(storeUserChemicalLibrary(userChemicalLibrary));
    };

    if (isEdit && userChemicalLibrary?.length === 0) {
      fetchUserChemicalLibrary();
    }
  }, []);

  useEffect(() => {
    saveItemSession(
      PENDING_PLANT_BASELINE_UNITS_KEY,
      JSON.stringify(baselineUnits),
    );
  }, [baselineUnits]);

  useEffect(() => {
    return () => {
      removeItemSession(PENDING_PLANT_BASELINE_UNITS_KEY);
      removeItemSession(PENDING_USER_CHEMICALS_TO_BE_ADDED);
      removeItemSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY);
    };
  }, []);

  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((item) => ({
          label: item.label,
          value: item.label,
        })),
    [],
  );

  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("isEdit");
  const plantId = searchParams.get("plantId");

  const storeInitialConfig = (trains) => {
    if (!trains) return;
    const result = {};
    Object.values(trains).forEach((train) => {
      const trainId = train.train_id;
      result[trainId] = {};
      train.passes.forEach((pass) => {
        const passId = pass.pass_id;
        result[trainId][passId] = {};
        pass.stages.forEach((stage) => {
          const stageId = stage.stage_id;
          const numElements =
            Number(stage.num_pressure_vessels) *
            Number(stage.num_element_per_pressure_vessel);
          result[trainId][passId][stageId] = numElements;
        });
      });
    });
    setLocalStorageItem("prevTrainConfig", JSON.stringify(result));
  };

  function normalizeRoNfElementUsed(trains) {
    const newTrains = JSON.parse(JSON.stringify(trains));
    for (const train of Object.values(newTrains)) {
      if (Object.prototype.hasOwnProperty.call(train, "ro_nf_elements")) {
        delete train.ro_nf_elements;
      }
      for (const pass of train.passes) {
        for (const stage of pass.stages) {
          if (stage.is_isd) {
            if (!Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used
                ? [stage.ro_nf_element_used]
                : [];
            }
          } else {
            if (Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used[0] || "";
            } else if (!stage.ro_nf_element_used) {
              stage.ro_nf_element_used = "";
            }
          }
        }
      }
    }
    return newTrains;
  }

  const selectedElementsCommentsData = useMemo(() => {
    if (!trains || Object.keys(trains).length === 0) {
      return [];
    }

    // Group all train-stage elements by associated comment text.
    const commentsMap = new Map();

    Object.values(trains).forEach((train) => {
      const trainRONFElements =
        RONFElements.length > 0 ? RONFElements : train.ro_nf_elements;

      if (!Array.isArray(trainRONFElements) || trainRONFElements.length === 0) {
        return;
      }

      const commentsByElement = Object.fromEntries(
        trainRONFElements
          .filter((element) => element?.id && element?.comment)
          .map((element) => [element.id, element.comment]),
      );

      train.passes?.forEach((pass) => {
        pass.stages?.forEach((stage) => {
          const selectedElements = (
            Array.isArray(stage.ro_nf_element_used)
              ? stage.ro_nf_element_used
              : [stage.ro_nf_element_used]
          )
            .map((elementValue) =>
              typeof elementValue === "string" ? elementValue.trim() : "",
            )
            .filter(Boolean);

          selectedElements.forEach((elementValue) => {
            const comment = commentsByElement[elementValue];
            if (!comment) {
              return;
            }

            if (!commentsMap.has(comment)) {
              commentsMap.set(comment, new Set());
            }

            commentsMap.get(comment).add(elementValue);
          });
        });
      });
    });

    return Array.from(commentsMap, ([comment, elements]) => ({
      elements: Array.from(elements),
      comment,
    }));
  }, [trains, RONFElements]);

  const normalizeVolumeUnit = (unit) => {
    const normalized = String(unit || "").toLowerCase();

    if (normalized === "m3" || normalized === "m³") {
      return "m3";
    }

    return normalized;
  };

  const hasUnitChangesForUtilityCosts = () => {
    const previousCurrency = baselineUnits?.currency || "USD";
    const previousVolume = normalizeVolumeUnit(baselineUnits?.volume || "m³");
    const previousRate = Number(baselineUnits?.conversion_rate) || 1;

    const nextCurrency = currency || "USD";
    const nextVolume = normalizeVolumeUnit(volume || "m³");
    const nextRate = Number(conversion_rate) || 1;

    return (
      previousCurrency !== nextCurrency ||
      previousVolume !== nextVolume ||
      previousRate !== nextRate
    );
  };

  const hasCompleteUtilityCosts = (apiValues = {}) => {
    const formValues = toUtilityFormValues(apiValues);

    return ["priceOfWater", "costOfDowntime", "costOfElectricity"].every(
      (fieldKey) => {
        const rawValue = formValues[fieldKey];
        if (rawValue === null || rawValue === undefined || rawValue === "") {
          return false;
        }

        const parsedValue = Number(rawValue);
        return Number.isFinite(parsedValue);
      },
    );
  };

  const isAllZeroUtilityCosts = (apiValues = {}) => {
    if (!hasCompleteUtilityCosts(apiValues)) {
      return false;
    }

    const payload = toUtilityApiPayload(toUtilityFormValues(apiValues));
    return (
      payload.water_price === 0 &&
      payload.downtime_cost === 0 &&
      payload.energy_price === 0
    );
  };

  // Only in edit flow, clear pending WavePro utility costs after user manually saves utility costs.
  const handlePlantUtilityCostsSaved = () => {
    if (!isEdit) {
      return;
    }

    if (
      !pendingWaveProUtilityCosts ||
      Object.keys(pendingWaveProUtilityCosts).length === 0
    ) {
      return;
    }

    setPendingWaveProUtilityCosts({});
  };

  useEffect(() => {
    if (waterType === "" || waterType === multipleWaterTypeOption?.value) {
      return;
    }
    setRONFElements([]);
    let isCancelled = false;

    const fetchRONFElements = async () => {
      try {
        const options = await fetchRONFElementsWithCache(waterType);
        if (!isCancelled) {
          setRONFElements(options);
          setTrains((prevTrains) =>
            prevTrains.map((train) => ({
              ...train,
              ro_nf_elements: options,
            })),
          );
        }
      } catch (error) {
        if (!isCancelled) {
          createNotification(
            ERROR_NOTIFICATION,
            t("ValueStrings.somethingWentWrong"),
            "",
          );
          setRONFElements([]);
        }
      }
    };

    fetchRONFElements();

    return () => {
      isCancelled = true;
    };
  }, [waterType]);

  useEffect(() => {
    return () => {
      clearRONFElementsCache();
    };
  }, []);

  useEffect(() => {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          // Existing-plant edit flow should not reuse pending pre-create utility payloads.
          removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);

          const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
          const response = await getPlantDetails(plantId, false, sel_train);
          if (response && response.data) {
            const {
              name,
              description,
              city,
              country,
              num_trains,
              water_type,
              config,
              setting,
              utility_costs,
              chemical_selection,
            } = response.data;

            dispatch(updateSelectedTrain(sel_train));
            setFetchedPlanDetails(response.data);
            setPlantDetail({
              ...plantDetail,
              installationName: name,
              description,
              city,
            });
            setCountry(country);
            setNoOfTrains(num_trains);
            setWaterType(water_type);
            dispatch(
              storePlantDetails({
                plantName: name,
                water_type,
              }),
            );
            dispatch(storeUnits(setting));
            setBaselineUnits({
              currency: setting?.currency || "USD",
              volume: setting?.volume || "m³",
              conversion_rate: Number(setting?.conversion_rate) || 1,
            });

            // Prefer dedicated utility-cost endpoint when available, fallback to plant details.
            let resolvedUtilityCosts = null;
            try {
              const utilityResponse = await getPlantUtilityCosts();
              if (utilityResponse?.status === 200) {
                resolvedUtilityCosts = utilityResponse?.data || null;
              }
            } catch (utilityError) {
              resolvedUtilityCosts = null;
            }

            const utilitySourceForStore = hasCompleteUtilityCosts(
              resolvedUtilityCosts,
            )
              ? resolvedUtilityCosts
              : utility_costs;

            if (hasCompleteUtilityCosts(utilitySourceForStore)) {
              dispatch(
                storePlantUtilityCosts(
                  toStoreUtilityCosts(utilitySourceForStore),
                ),
              );
            }
            setTrains(config?.trains);
            storeInitialConfig(config?.trains);
            setChemicalSelection(chemical_selection);
          }
        } catch (error) {
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  // Helper to update a single section's status in mandatoryComponentsStatus
  const updateComponentStatus = (section, updates) => {
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...updates },
    }));
  };

  const applyPlantWaterTypeChange = async (nextWaterType) => {
    setWaterType(nextWaterType);
    setTrains((prevTrains) =>
      [...prevTrains].map((train) => {
        const updatedTrain = { ...train };
        updatedTrain.water_type = nextWaterType;

        return updatedTrain;
      }),
    );
    updateComponentStatus("plantInformation", { changed: true });
  };

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, country: "" });
    updateComponentStatus("plantInformation", { changed: true });
  };
  const changeWaterType = (value) => {
    if (value === waterType) {
      return;
    }

    setPendingPlantWaterType(value);
    setShowWaterTypeImpactWarning(true);
  };

  const handleWaterTypeImpactWarningClose = async (buttonId) => {
    setShowWaterTypeImpactWarning(false);

    if (buttonId === CustomModalButtons.CONFIRM) {
      setPlantDetailError({ ...plantDetailerror, waterType: "" });
      await applyPlantWaterTypeChange(pendingPlantWaterType);
    }

    setPendingPlantWaterType("");
  };

  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    setPlantDetailError({ ...plantDetailerror, [key]: "" });
    updateComponentStatus("plantInformation", { changed: true });
  };

  const isNonEmptySelectedElement = (selectedElement) =>
    String(selectedElement ?? "").trim() !== "";

  const hasCompleteIsdSelection = (selectedElements, elementsPerVessel) =>
    Number.isFinite(elementsPerVessel) &&
    elementsPerVessel >= 1 &&
    elementsPerVessel <= 8 &&
    Array.isArray(selectedElements) &&
    selectedElements.length === elementsPerVessel &&
    selectedElements.every(isNonEmptySelectedElement);

  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    let plantInfoValid = true;
    let roConfigValid = true;
    const error = [];
    // Validate Plant Information
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["installationName"] = t("addNewPlant.requiredFieldMessage");
    }
    if (!country) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["country"] = t("addNewPlant.requiredFieldMessage");
    }
    if (!waterType) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["waterType"] = t("addNewPlant.requiredFieldMessage");
    }

    // Update plant information status
    updateComponentStatus("plantInformation", {
      fulfilled: plantInfoValid,
      changed: false,
      error: !plantInfoValid,
    });

    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    // Validate RO Configuration (required + range) and build train/pass context.
    const invalidPassByTrain = {};

    for (const train of Object.values(trains || {})) {
      const trainId = train?.train_id;
      const passes = train?.passes || [];

      if (!passes.length) {
        validationSuccessful = false;
        roConfigValid = false;
        if (trainId) {
          invalidPassByTrain[trainId] = [];
        }
        continue;
      }

      for (const pass of passes) {
        const passId = pass?.pass_id;
        let passInvalid = !pass?.stages?.length;

        if (!passInvalid) {
          for (const stage of pass.stages) {
            const numPressureVessels = Number(stage?.num_pressure_vessels);
            const numElementsPerVessel = Number(
              stage?.num_element_per_pressure_vessel,
            );

            const hasValidPressureVessels =
              Number.isFinite(numPressureVessels) &&
              numPressureVessels >= 1 &&
              numPressureVessels <= 100000;
            const hasValidElementsPerVessel =
              Number.isFinite(numElementsPerVessel) &&
              numElementsPerVessel >= 1 &&
              numElementsPerVessel <= 8;

            const hasValidElement = stage?.is_isd
              ? hasCompleteIsdSelection(
                  stage?.ro_nf_element_used,
                  numElementsPerVessel,
                )
              : typeof stage?.ro_nf_element_used === "string" &&
                stage.ro_nf_element_used.trim() !== "";

            if (
              !hasValidPressureVessels ||
              !hasValidElementsPerVessel ||
              !hasValidElement
            ) {
              passInvalid = true;
              break;
            }
          }
        }

        if (passInvalid) {
          validationSuccessful = false;
          roConfigValid = false;
          if (trainId) {
            if (!invalidPassByTrain[trainId]) {
              invalidPassByTrain[trainId] = [];
            }
            if (passId && !invalidPassByTrain[trainId].includes(passId)) {
              invalidPassByTrain[trainId].push(passId);
            }
          }
        }
      }
    }

    setRoValidationContext({
      show: !roConfigValid,
      invalidPassByTrain,
    });

    // Update RO configuration status
    updateComponentStatus("roConfiguration", {
      fulfilled: roConfigValid,
      changed: false,
      error: !roConfigValid,
    });

    return validationSuccessful;
  };

  const mapChemicalSelectionWithChemIds = (refreshedPlantChemicalLibrary) => {
    const chemIdByDisplayName = getChemIdByDisplayName(
      refreshedPlantChemicalLibrary,
    );

    const updatedChemicalSelection = Object.fromEntries(
      Object.entries(chemicalSelection).map(
        ([chemicalCategory, selectedChemical]) => {
          const chemId = chemIdByDisplayName.get(
            selectedChemical?.chemical_name,
          );
          return [
            chemicalCategory,
            chemId
              ? { ...selectedChemical, chem_id: chemId }
              : selectedChemical,
          ];
        },
      ),
    );
    setChemicalSelection(updatedChemicalSelection);
  };

  const getChemicalsFromSession = (sessionKey) => {
    const pendingChemicalsRaw = getItemSession(sessionKey);
    if (!pendingChemicalsRaw) {
      return [];
    }
    return JSON.parse(pendingChemicalsRaw);
  };

  const getChemIdByDisplayName = (chemicalLibrary = []) =>
    new Map(
      (Array.isArray(chemicalLibrary) ? chemicalLibrary : [])
        .filter((chemical) => chemical?.display_name && chemical?.chem_id)
        .map((chemical) => [chemical.display_name, chemical.chem_id]),
    );

  const updatePendingPlantChemicalsWithChemIds = (
    refreshedPlantChemicalLibrary,
  ) => {
    const pendingPlantChemicals =
      getChemicalsFromSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY)?.chemicals ||
      [];

    if (pendingPlantChemicals.length === 0) {
      return;
    }

    const chemIdByDisplayName = getChemIdByDisplayName(
      refreshedPlantChemicalLibrary,
    );

    const updatedPendingPlantChemicals = pendingPlantChemicals.map(
      (chemical) => {
        if (chemical?.chem_id) {
          return chemical;
        }

        const resolvedChemId = chemIdByDisplayName.get(chemical?.display_name);

        return resolvedChemId
          ? { ...chemical, chem_id: resolvedChemId }
          : chemical;
      },
    );

    saveItemSession(
      PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
      JSON.stringify(updatedPendingPlantChemicals),
    );
  };

  const syncPendingImportWaveProAddedChemicals = async (
    pendingChemicalsToBeAdded,
  ) => {
    if (!pendingChemicalsToBeAdded || pendingChemicalsToBeAdded.length === 0) {
      return [];
    }

    try {
      const response = await updateChemicalLibraryData(
        pendingChemicalsToBeAdded,
        t,
        true,
        false,
        false,
      );

      if (response === null) {
        throw new Error();
      }

      removeItemSession(PENDING_USER_CHEMICALS_TO_BE_ADDED);
      // refresh both plant & user chemical library data in store after import
      const refreshedUserChemicalLibrary = await fetchChemicalLibraryData(
        t,
        true,
      );
      dispatch(storeUserChemicalLibrary(refreshedUserChemicalLibrary));
      let refreshedPlantChemicalLibrary = refreshedUserChemicalLibrary;

      if (isEdit) {
        refreshedPlantChemicalLibrary = await fetchChemicalLibraryData(t);
        dispatch(storePlantChemicalLibrary(refreshedPlantChemicalLibrary));
      } else {
        // if plant doesn't exist, we will update the plant chemical library with user chemical library
        dispatch(storePlantChemicalLibrary(refreshedUserChemicalLibrary));
      }

      updatePendingPlantChemicalsWithChemIds(refreshedPlantChemicalLibrary);
      return refreshedPlantChemicalLibrary;
    } catch (error) {
      throw new Error(
        error.message || "Failed to sync pending WavePro added chemicals",
      );
    }
  };

  const notifySaveError = (statusCode, source) => {
    if (
      statusCode === 400 &&
      source?.detail?.key === API_RESPONSE_CODE.PLANT_ALREADY_EXISTS
    ) {
      createNotification(
        ERROR_NOTIFICATION,
        t("addNewPlant.plantAlreadyExists"),
        "",
      );
    } else if (statusCode === 422) {
      createNotification(
        ERROR_NOTIFICATION,
        source?.detail ||
          source?.message ||
          t("notificationStrings.somethingWrong"),
        "",
      );
    } else {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  const savePlantDetails = async (key, opts = {}) => {
    if (!opts.skipLoading) setIsLoading(true);
    setIsSaving(true);
    try {
      const stringifyCostValues = (cost = {}) => {
        Object.keys(cost).forEach((costKey) => {
          cost[costKey] = String(cost[costKey]);
        });
      };

      const syncPendingPayload = async ({
        payload,
        sessionKey,
        syncApi,
        failedServiceName,
        failedServices,
      }) => {
        let resolvedPayload = payload;

        if (!resolvedPayload && sessionKey) {
          const pendingPayload = getItemSession(sessionKey);
          if (!pendingPayload) {
            return false;
          }
          resolvedPayload = JSON.parse(pendingPayload);
        }

        if (!resolvedPayload || Object.keys(resolvedPayload).length === 0) {
          return false;
        }

        try {
          const syncResponse = await syncApi(resolvedPayload);
          if (syncResponse?.status !== 200) {
            throw new Error(
              `${failedServiceName} sync failed with status ${syncResponse?.status}`,
            );
          }

          if (sessionKey) {
            removeItemSession(sessionKey);
          }

          return true;
        } catch (syncError) {
          failedServices.push(failedServiceName);
          return true;
        }
      };

      const syncConvertedUtilityCostsIfNeeded = async (failedServices) => {
        if (!hasUnitChangesForUtilityCosts()) {
          return false;
        }

        const utilityServiceName = t("plantLevel.utilityCosts");

        try {
          const pendingUtilityCosts = getItemSession(
            PENDING_PLANT_UTILITY_COSTS_KEY,
          );

          let sourceUtilityCosts = null;

          if (pendingUtilityCosts) {
            const pendingSource = JSON.parse(pendingUtilityCosts);
            if (hasCompleteUtilityCosts(pendingSource)) {
              sourceUtilityCosts = pendingSource;
            }
          } else if (
            plantUtilityCosts?.water_price !== undefined ||
            plantUtilityCosts?.energy_price !== undefined ||
            plantUtilityCosts?.downtime_cost !== undefined
          ) {
            sourceUtilityCosts = plantUtilityCosts;
          }

          const shouldRefreshFromApi =
            !sourceUtilityCosts || isAllZeroUtilityCosts(sourceUtilityCosts);

          if (shouldRefreshFromApi) {
            // Last-resort fallback for flows where utility values were never loaded into store.
            const utilityResponse = await getPlantUtilityCosts();
            if (utilityResponse?.status === 200) {
              const fetchedUtilityCosts = utilityResponse?.data || {};

              if (hasCompleteUtilityCosts(fetchedUtilityCosts)) {
                sourceUtilityCosts = fetchedUtilityCosts;
                dispatch(
                  storePlantUtilityCosts(
                    toStoreUtilityCosts(fetchedUtilityCosts),
                  ),
                );
              }
            }
          }

          if (!sourceUtilityCosts) {
            return false;
          }

          if (!hasCompleteUtilityCosts(sourceUtilityCosts)) {
            throw new Error("Incomplete utility costs source for conversion");
          }

          const convertedFormValues = convertUtilityFormValues(
            toUtilityFormValues(sourceUtilityCosts),
            {
              currency: baselineUnits?.currency,
              volume: baselineUnits?.volume,
              conversion_rate: baselineUnits?.conversion_rate,
            },
            {
              currency,
              volume,
              conversion_rate,
            },
          );

          const convertedPayload = toUtilityApiPayload(convertedFormValues);
          const plantUtilityUpdateResponse =
            await updatePlantUtilityCosts(convertedPayload);

          if (plantUtilityUpdateResponse?.status !== 200) {
            throw new Error(
              `Utility cost conversion sync failed with status ${plantUtilityUpdateResponse?.status}`,
            );
          }

          dispatch(storePlantUtilityCosts(convertedPayload));
          removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);

          return true;
        } catch (syncError) {
          failedServices.push(utilityServiceName);
          return true;
        }
      };

      const syncPendingImportWaveProUtilityCosts = async (failedServices) => {
        if (
          !pendingWaveProUtilityCosts ||
          Object.keys(pendingWaveProUtilityCosts).length === 0
        ) {
          return false;
        }

        const utilityServiceName = t("plantLevel.utilityCosts");

        let mergedPendingPayload = { ...pendingWaveProUtilityCosts };
        const utilityPendingRaw = getItemSession(
          PENDING_PLANT_UTILITY_COSTS_KEY,
        );
        if (utilityPendingRaw) {
          const utilityPendingPayload = JSON.parse(utilityPendingRaw);
          // Utility dialog updates should win over imported defaults for overlapping keys.
          mergedPendingPayload = {
            ...mergedPendingPayload,
            ...utilityPendingPayload,
          };
        }

        const utilitySyncHandled = await syncPendingPayload({
          payload: mergedPendingPayload,
          syncApi: updatePlantUtilityCosts,
          failedServiceName: utilityServiceName,
          failedServices,
        });

        if (utilitySyncHandled) {
          setPendingWaveProUtilityCosts({});
          if (utilityPendingRaw) {
            removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);
          }
        }

        return utilitySyncHandled;
      };

      const syncPendingImportWaveProChemicals = async (failedServices) => {
        const pendingPlantChemicalData =
          getChemicalsFromSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY)
            .chemicals || [];

        if (
          (!pendingPlantChemicalData ||
            pendingPlantChemicalData.length === 0) &&
          (!pendingChemicalsToBeEdited ||
            pendingChemicalsToBeEdited.length === 0)
        ) {
          return false;
        }

        const chemicalServiceName = t("plantLevel.chemicalLibrary");

        const mergedChemicals = [
          ...(pendingPlantChemicalData || []),
          ...(pendingChemicalsToBeEdited || []),
        ];

        const editedChemicalsPayload = mergedChemicals.map((chemical) => {
          const chemId = Number(chemical?.chem_id);
          if (!chemId) {
            return null;
          }
          return {
            chem_id: chemId,
            price: Number(chemical?.price),
            unit: chemical?.unit,
          };
        });

        const chemicalPayload = {
          chemicals: editedChemicalsPayload.filter(Boolean),
        };

        if (!chemicalPayload.chemicals.length) {
          return false;
        }

        const chemicalSyncHandled = await syncPendingPayload({
          payload: chemicalPayload,
          syncApi: updatePlantChemicalLibrary,
          failedServiceName: chemicalServiceName,
          failedServices,
        });

        if (chemicalSyncHandled) {
          removeItemSession(PENDING_PLANT_CHEMICAL_LIBRARY_KEY);
          setPendingChemicalsToBeEdited([]);
        }

        return chemicalSyncHandled;
      };

      const validated = runValidation();
      setStageValidationSucessfull(validated);
      let response = null;
      if (validated) {
        let chemicalSelectionPayload = chemicalSelection;

        // Create custom chemical before SAVE plant call to get chemical ids
        const pendingChemicalsToBeAdded = getChemicalsFromSession(
          PENDING_USER_CHEMICALS_TO_BE_ADDED,
        );
        if (pendingChemicalsToBeAdded && pendingChemicalsToBeAdded.length > 0) {
          const refreshedPlantChemicalLibrary =
            await syncPendingImportWaveProAddedChemicals(
              pendingChemicalsToBeAdded,
            );

          const hasMissingChemId = Object.values(chemicalSelection).some(
            (selectedChemical) => !selectedChemical?.chem_id,
          );

          if (hasMissingChemId) {
            mapChemicalSelectionWithChemIds(refreshedPlantChemicalLibrary);
          }
        }

        const activePlantId = plantId || createdPlantId;
        // Normalize ro_nf_element_used for all stages before building payload
        const normalizedTrains = normalizeRoNfElementUsed(trains);
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_quality: feed_quality,
            feed_water_factor: feed_water_factor,
            permeate_quality: permeate_quality,
            feed_water_permeate_factor: feed_water_permeate_factor,
            temperature: temperature,
            volume: volume,
            mass: mass,
            currency: currency,
            conversion_rate: conversion_rate,
          },
          config: {
            trains: [...Object.values(normalizedTrains)],
          },
          tol_num_elements: totalNumberOfElements,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("plantName", plantDetail.installationName);

        // Edit flow with existing plantId
        if (plantId && isEdit === "true") {
          const editedFields = getChangedData(payload, fetchedPlanDetails);
          // For changed chem_id, send full item (chem_id, chemical_name, price).
          const changedChemicalSelection = {};
          Object.keys(chemicalSelectionPayload).forEach((chemicalCategory) => {
            const updatedChemical =
              chemicalSelectionPayload?.[chemicalCategory];
            const previousChemical =
              fetchedPlanDetails?.chemical_selection?.[chemicalCategory];

            const updatedChemId = Number(updatedChemical?.chem_id ?? "");
            const previousChemId = Number(previousChemical?.chem_id ?? "");

            if (updatedChemId !== previousChemId) {
              changedChemicalSelection[chemicalCategory] = {
                chem_id: updatedChemical?.chem_id,
                chemical_name: updatedChemical?.chemical_name,
                price: updatedChemical?.price,
              };
            }
          });
          if (Object.keys(changedChemicalSelection).length > 0) {
            editedFields.chemical_selection = changedChemicalSelection;
          }
          // Convert cost values to strings for API if they exist in editedFields
          if (editedFields.cost) {
            stringifyCostValues(editedFields.cost);
          }
          response = await editPlant(plantId, editedFields);
        }
        // Case: savePlant succeeded, but post-create chemical lib or utility cost sync failed.
        // User will not be navigated away. On retry, we have to use created plant ID to avoid duplicate plant creation.
        else if (activePlantId) {
          if (payload.cost) {
            stringifyCostValues(payload.cost);
          }
          response = await editPlant(activePlantId, payload);
        } // new plant creation flow
        else {
          // Convert cost values to strings for API
          if (payload.cost) {
            stringifyCostValues(payload.cost);
          }
          payload.chemical_selection = chemicalSelectionPayload;
          response = await savePlant(payload);
        }
        const statusCode = Number(response?.status);
        if (statusCode !== 200) {
          const error = new Error();
          error.response = { status: statusCode, data: response };
          throw error;
        }

        const resolvedPlantId = response?.plant_id || activePlantId;

        if (resolvedPlantId) {
          setCreatedPlantId(resolvedPlantId);
          updatePlantId(resolvedPlantId);

          let failedServices = [];

          const chemicalSyncHandledByImport =
            await syncPendingImportWaveProChemicals(failedServices);

          if (!chemicalSyncHandledByImport) {
            await syncPendingPayload({
              sessionKey: PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
              syncApi: updatePlantChemicalLibrary,
              failedServiceName: t("plantLevel.chemicalLibrary"),
              failedServices,
            });
          }

          const utilitySyncHandledByImport =
            await syncPendingImportWaveProUtilityCosts(failedServices);

          const utilitySyncHandledByConversion = utilitySyncHandledByImport
            ? false
            : await syncConvertedUtilityCostsIfNeeded(failedServices);

          if (!utilitySyncHandledByImport && !utilitySyncHandledByConversion) {
            await syncPendingPayload({
              sessionKey: PENDING_PLANT_UTILITY_COSTS_KEY,
              syncApi: updatePlantUtilityCosts,
              failedServiceName: t("plantLevel.utilityCosts"),
              failedServices,
            });
          }

          if (failedServices.length > 0 && key === "continue") {
            createNotification(
              ERROR_NOTIFICATION,
              t("addNewPlant.postCreateSyncFailed", {
                services: failedServices.join(t("valueStrings.and")),
              }),
              "",
            );
            return;
          }

          // Reset all changed flags after successful save
          setMandatoryComponentsStatus((prev) =>
            Object.fromEntries(
              Object.entries(prev).map(([key, val]) => [
                key,
                { ...val, changed: false },
              ]),
            ),
          );

          setBaselineUnits({
            currency,
            volume,
            conversion_rate: Number(conversion_rate) || 1,
          });
          removeItemSession(SESSION_STORAGE_KEYS.NO_OF_TRAINS_KEY);

          // Show success notification for both save and continue, and save and exit
          createNotification(
            SUCCESS_NOTIFICATION,
            t("addNewPlant.successfullySaved"),
            "",
          );

          if (key === "exit") {
            if (accessUserPlant) {
              dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
              navigate("/user-management");
            } else {
              navigate("/plants");
            }
          } else if (!opts.skipNavigate) {
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${resolvedPlantId}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${resolvedPlantId}`;
            navigate(path);
          }
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.somethingWrong"),
            "",
          );
        }
      }
    } catch (error) {
      const statusCode = Number(error?.response?.status ?? error?.status);
      const errorSource = error?.response?.data || error;
      notifySaveError(statusCode, errorSource);
    } finally {
      if (!opts.skipLoading) setIsLoading(false);
      setIsSaving(false);
    }
  };

  if (typeof window !== "undefined") {
    window.savePlantDetails = savePlantDetails;
  }

  const isStageConfigured = (stage = {}) => {
    const {
      num_pressure_vessels,
      num_element_per_pressure_vessel,
      is_isd,
      ro_nf_element_used,
    } = stage;
    const hasValidCounts =
      Number(num_pressure_vessels) > 0 &&
      Number(num_element_per_pressure_vessel) > 0;
    const hasValidElement = is_isd
      ? hasCompleteIsdSelection(
          ro_nf_element_used,
          Number(num_element_per_pressure_vessel),
        )
      : typeof ro_nf_element_used === "string" &&
        ro_nf_element_used.trim() !== "";
    return hasValidCounts && hasValidElement;
  };

  const isTrainConfigured = (train = {}) =>
    train?.passes?.length > 0 &&
    train.passes.every(
      (pass) =>
        pass?.stages?.length > 0 && pass.stages.every(isStageConfigured),
    );

  const hasAtLeastOneConfiguredTrain = useMemo(
    () => Object.values(trains || {}).some(isTrainConfigured),
    [trains],
  );

  const hasMultipleTrains = useMemo(
    () => Object.values(trains || {}).length > 1,
    [trains],
  );

  const handleImportTrainConfigClick = () => {
    setIsTrainConfigModalOpen(true);
  };

  const handleImportPlantClick = () => {
    if (isEdit) {
      setShowImportPlantConfirmation(true);
      return;
    }
    setIsImportPlantModalOpen(true);
  };

  const handleImportPlantConfirmationClose = (buttonId) => {
    setShowImportPlantConfirmation(false);

    if (buttonId === CustomModalButtons.CONFIRM) {
      setIsImportPlantModalOpen(true);
    }
  };

  const handleImportPlantModalClose = () => {
    setIsImportPlantModalOpen(false);
  };

  const handleExtractPlantConfig = () => {
    setIsImportPlantModalOpen(false);
  };

  const handleTrainConfigModalClose = () => {
    setIsTrainConfigModalOpen(false);
  };

  const handleImportTrainConfig = (sourceTrainId, targetTrainIds) => {
    if (!sourceTrainId || !Array.isArray(targetTrainIds)) return;

    const sourceTrain = Object.values(trains || {}).find(
      (t) => t?.train_id === sourceTrainId,
    );
    if (!sourceTrain) return;

    const clonePasses = () =>
      JSON.parse(JSON.stringify(sourceTrain.passes || []));
    const updatedTrains = Object.values(trains || {}).map((train) =>
      targetTrainIds.includes(train.train_id)
        ? {
            ...train,
            is_dupont_element: sourceTrain.is_dupont_element,
            passes: clonePasses(),
          }
        : train,
    );

    setTrains(updatedTrains);

    // Re-validate: check if all trains are now configured
    const allTrainsConfigured =
      Object.values(updatedTrains).every(isTrainConfigured);
    if (allTrainsConfigured) {
      setStageValidationSucessfull(true);
    }

    updateComponentStatus("roConfiguration", { changed: true });
  };

  const hasUnsavedChanges = useMemo(() => {
    // Don't block navigation during save operations
    if (isSaving) return false;

    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus, isSaving]);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  useEffect(() => {
    if (!Array.isArray(trains) || trains.length <= 0) {
      return;
    }

    const waterTypesInTrains = new Set(
      trains.map((train) => train.water_type).filter(Boolean),
    );

    if (waterTypesInTrains.size === 1) {
      setWaterType(waterTypesInTrains.values().next().value);
    } else if (waterTypesInTrains.size > 1) {
      setWaterType(multipleWaterTypeOption?.value);
      setRONFElements([]);
    }
  }, [trains]);

  const buildTrainFromWaveProSelection = ({
    selectedCase,
    selectedRODesign,
    trainId,
  }) => {
    const mappedPasses = selectedRODesign?.roConfiguration?.passes || [];

    return {
      train_id: trainId,
      passes: mappedPasses.map((pass, passIndex) => ({
        pass_id: passIndex + 1,
        stages: (pass?.stages || []).map((stage, stageIndex) => ({
          stage_id: stageIndex + 1,
          num_pressure_vessels: Number(stage?.pressureVessels),
          num_element_per_pressure_vessel: Number(stage?.noOfElements),
          ro_nf_element_used:
            stage?.roNfElements.length === 1
              ? stage?.roNfElements?.[0]
              : stage?.roNfElements,
          installation_year: "",
          is_isd: stage?.roNfElements.length > 1,
        })),
      })),
      is_dupont_element: true, // wavepro doesn't have a concept of non-dupont elements, so we default to true
      water_type: selectedRODesign?.feedWaterType,
      case_notes: selectedCase?.caseNote,
    };
  };

  const syncPendingUserChemicalLibrary = (
    chemicalsToBeAdded,
    chemicalsToBeEdited,
  ) => {
    // Add new chemicals
    const pendingUserChemicalLibrary = [
      ...userChemicalLibrary,
      ...chemicalsToBeAdded,
    ];
    const pendingPlantChemicalLibrary = [
      ...plantChemicalLibrary,
      ...chemicalsToBeAdded,
    ];

    // Edit existing chemicals
    chemicalsToBeEdited.forEach((chemical) => {
      const index = pendingPlantChemicalLibrary.findIndex(
        (c) => c.id === chemical.id,
      );
      if (index !== -1) {
        pendingPlantChemicalLibrary[index] = {
          ...pendingPlantChemicalLibrary[index],
          ...chemical,
        };
      }
    });

    dispatch(storeUserChemicalLibrary(pendingUserChemicalLibrary));
    dispatch(storePlantChemicalLibrary(pendingPlantChemicalLibrary));

    saveItemSession(
      PENDING_USER_CHEMICALS_TO_BE_ADDED,
      JSON.stringify(chemicalsToBeAdded),
    );

    setPendingChemicalsToBeEdited(chemicalsToBeEdited);

    return pendingPlantChemicalLibrary;
  };

  const applyWaveProConfig = (
    waveProConfig,
    chemicalData,
    trainCaseRoMap,
    normalizedSettingsAndCosts,
  ) => {
    if (!waveProConfig) return;

    // sync pending user, plant chemical libary
    const pendingPlantChemicalLibrary = syncPendingUserChemicalLibrary(
      chemicalData.chemicalsToBeAdded,
      chemicalData.chemicalsToBeEdited,
    );

    const { project, cases } = waveProConfig;

    const { name, city, country, description } = project;
    // plant information
    setPlantDetail({
      ...plantDetail,
      installationName: name,
      description,
      city,
    });
    setCountry(country);
    updateComponentStatus("plantInformation", { changed: true });
    setPlantDetailError({});

    // ro configuration
    setTrains((previousTrains) => {
      const nextTrains = Array.isArray(previousTrains)
        ? [...previousTrains]
        : [];

      Object.keys(trainCaseRoMap || {}).forEach((trainKey) => {
        const selection = trainCaseRoMap?.[trainKey];
        if (!selection) {
          return;
        }

        const trainId = Number(String(trainKey).replace("train", ""));
        const trainIndex = trainId - 1;
        if (!Number.isFinite(trainId) || trainId < 1) {
          return;
        }

        const { caseName, roName } = selection;
        if (!caseName || !roName) {
          return;
        }

        const selectedCase = (cases || []).find(
          (waveCase) => waveCase?.caseName === caseName,
        );
        if (!selectedCase) {
          return;
        }

        const selectedRODesign = (selectedCase?.roTreatments || []).find(
          (treatment) => treatment?.nodeId === roName,
        );
        if (!selectedRODesign) {
          return;
        }

        nextTrains[trainIndex] = buildTrainFromWaveProSelection({
          selectedCase,
          selectedRODesign,
          trainId,
        });
      });

      return nextTrains;
    });
    // Explicit trigger for child-side reconcile when import applies new train config.
    setRoReconcileTrigger((prev) => prev + 1);
    updateComponentStatus("roConfiguration", { changed: true });

    // units & currency
    const { normalizedSettings, normalizedUtilityCosts } =
      normalizedSettingsAndCosts;
    const settingsForStore = {
      ...normalizedSettings,
    };

    if (normalizedSettings.feed_quality === UNITS.uscm) {
      settingsForStore.feed_quality = "uS/cm";
      settingsForStore.permeate_quality = "uS/cm";
    }
    if (settingsForStore.temperature === UNITS.celsius) {
      settingsForStore.temperature = "C";
    } else if (settingsForStore.temperature === UNITS.farhenite) {
      settingsForStore.temperature = "F";
    }

    dispatch(storeUnits(settingsForStore));
    // Keep baseline in sync with imported units to avoid re-converting imported utility costs on save.
    setBaselineUnits({
      currency: settingsForStore.currency,
      volume: settingsForStore.volume,
      conversion_rate: Number(settingsForStore.conversion_rate),
    });
    updateComponentStatus("settings", { changed: true });

    // chemical selection
    // Update chemical selection with chem_id from userChemicalLibrary based on chemical_name after chemicalCreation

    const updatedSelection = { ...chemicalData.chemicalSelection };
    Object.keys(updatedSelection).forEach((key) => {
      const userChemical = pendingPlantChemicalLibrary.find(
        (chem) => chem.display_name === updatedSelection[key].chemical_name,
      );
      updatedSelection[key] = {
        ...chemicalData.chemicalSelection[key],
        chem_id: userChemical?.chem_id,
      };
    });

    setChemicalSelection(updatedSelection);
    updateComponentStatus("chemicalsUsedForCleaning", { changed: true });

    // utility costs
    dispatch(storePlantUtilityCosts(normalizedUtilityCosts));
    setPendingWaveProUtilityCosts(normalizedUtilityCosts);
  };

  return (
    <div className="container-main">
      <Header
        showPlantMenu
        isLoading={isLoading}
        onPlantUtilityCostsSaved={handlePlantUtilityCostsSaved}
        navigationConfig={{
          onPreviousClick: () => savePlantDetails("exit"),
          onNextClick: () => savePlantDetails("continue"),
        }}
      />
      <PageNavigation />

      <div className="main-container">
        <div className="content-container descriptionText">
          {isLoading ? (
            <div className="circular-loader-overlay">
              <CircularLoader />
            </div>
          ) : (
            <div>
              <div className="flex-between-center">
                <div
                  className="calculatedValueText"
                  id="tour-add-plant-intro"
                ></div>
                <div className="new-badge-inline-wrapper">
                  <WPTertiaryButton
                    id="import-plant-btn"
                    label={t("addNewPlant.importPlantWave")}
                    icon={<DataAppendIcon />}
                    iconPlacement="before"
                    size="small"
                    className="import-train-config-btn"
                    onClick={handleImportPlantClick}
                    disabled={
                      plantChemicalLibrary?.length === 0 ||
                      userChemicalLibrary?.length === 0
                    }
                  />
                  <NewBadgeIcon className="new-badge" />
                </div>
              </div>
              <div className="ro-card-wrapper position-relative">
                <div className="ro-card-header">
                  <div className="teal-heading ">
                    {t("plantInformationStrings.plantInfo")}
                  </div>
                </div>
                <div className="grid-container-row plant-info-grid-four">
                  <WPInput
                    id="add-plant-installation-name"
                    name="installationName"
                    value={plantDetail["installationName"]}
                    label={t(`addNewPlant.${"installationName"}`)}
                    placeholder={t(`addNewPlant.${"installationName"}`)}
                    required
                    onChange={(event) =>
                      handleInputChange("installationName", event.target.value)
                    }
                    externalError={plantDetailerror["installationName"]}
                    helperText={plantDetailerror["installationName"]}
                    maxLength={250}
                  />
                  <WPInput
                    id="add-plant-city"
                    name="city"
                    value={plantDetail["city"]}
                    label={t(`addNewPlant.${"city"}`)}
                    placeholder={t(`addNewPlant.${"city"}`)}
                    onChange={(event) =>
                      handleInputChange("city", event.target.value)
                    }
                    externalError={plantDetailerror["city"]}
                    helperText={plantDetailerror["city"]}
                    maxLength={100}
                  />
                  <WPDropdown
                    id="add-plant-country"
                    name="country"
                    label={t("addNewPlant.country")}
                    placeholder={t("addNewPlant.country")}
                    value={country}
                    onChange={(selectedOption) =>
                      changeCountry(selectedOption?.value || "")
                    }
                    options={countryOptions}
                    isError={Boolean(plantDetailerror["country"])}
                    helperText={plantDetailerror["country"]}
                    required
                  />
                  <WPDropdown
                    id="add-plant-water-type"
                    name="waterType"
                    label={t("addNewPlant.waterType")}
                    placeholder={t("addNewPlant.waterType")}
                    value={waterType}
                    onChange={(selectedOption) =>
                      changeWaterType(selectedOption?.value || "")
                    }
                    options={[...waterTypeOptions, multipleWaterTypeOption]}
                    filterOption={(option, inputValue) => {
                      // Hide Multiple from dropdown list
                      if (option.data.value === "Multiple") {
                        return false;
                      }

                      return option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase());
                    }}
                    isError={Boolean(plantDetailerror["waterType"])}
                    helperText={plantDetailerror["waterType"]}
                    required
                  />
                </div>

                <WPTextArea
                  id="add-plant-description"
                  value={plantDetail["description"]}
                  label={t(`addNewPlant.${"description"}`)}
                  placeholder={t(`addNewPlant.${"description"}`)}
                  rows={3}
                  required={false}
                  onChange={(value) => handleInputChange("description", value)}
                  maxCount={500}
                />
              </div>
              <div>
                <div className="ro-card-wrapper position-relative">
                  <div className="ro-card-header">
                    <div className="info-section">
                      <div className="teal-heading">
                        {t("addNewPlant.trainConfiguration")}
                      </div>
                      <IconWithTooltip
                        label={t("helpInformation.roConfigurationHelp")}
                      />
                    </div>
                    <WPTertiaryButton
                      id="import-train-config-btn"
                      label={t("importTrainConfig.title")}
                      icon={<NodeExtensionIcon />}
                      iconPlacement="before"
                      size="small"
                      className="import-train-config-btn"
                      disabled={
                        !hasAtLeastOneConfiguredTrain || !hasMultipleTrains
                      }
                      onClick={handleImportTrainConfigClick}
                    />
                  </div>
                  <>
                    <div className="description-text">
                      <RoConfiguration
                        trains={trains}
                        setTrains={setTrains}
                        totalNumberOfElements={totalNumberOfElements}
                        setTotalNumberOfElements={setTotalNumberOfElements}
                        stageValidationSucessfull={stageValidationSucessfull}
                        setStageValidationSucessfull={
                          setStageValidationSucessfull
                        }
                        noOfTrains={noOfTrains}
                        RONFElements={RONFElements}
                        roValidationContext={roValidationContext}
                        setMandatoryComponentsStatus={
                          setMandatoryComponentsStatus
                        }
                        waterTypeOptions={waterTypeOptions}
                        roReconcileTrigger={roReconcileTrigger}
                      />
                    </div>
                    {/* These comments are for all trains special elements, not for individual stages or passes. */}
                    <CommentsOnSpecialElements
                      commentsData={selectedElementsCommentsData}
                    />
                  </>
                </div>
                <Settings
                  disabled={false}
                  setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                />
                <div id="chemical-selection-fields">
                  <ChemicalsUsedForCleaning
                    chemicalsUsed={chemicalSelection}
                    onChemicalsChange={setChemicalSelection}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                  />
                </div>
                {isTrainConfigModalOpen && (
                  <ImportTrainConfigModal
                    show={isTrainConfigModalOpen}
                    trains={trains}
                    onClose={handleTrainConfigModalClose}
                    onImport={handleImportTrainConfig}
                    isTrainConfigured={isTrainConfigured}
                  />
                )}
                {isImportPlantModalOpen && (
                  <ImportPlantWavePro
                    show={isImportPlantModalOpen}
                    onClose={handleImportPlantModalClose}
                    onExtract={handleExtractPlantConfig}
                    noOfTrains={noOfTrains}
                    isEditFlow={isEdit}
                    applyWaveProConfig={applyWaveProConfig}
                  />
                )}
                {showImportPlantConfirmation && (
                  <ConfirmationPopup
                    type="warning"
                    header={t("importPlantWavePro.overwriteConfirmHeader")}
                    description={t(
                      "importPlantWavePro.overwriteConfirmDescription",
                    )}
                    onClose={handleImportPlantConfirmationClose}
                    cancelBtn={t("ValueStrings.cancel")}
                    confirmBtn={t("ValueStrings.proceed")}
                  />
                )}
                {showWaterTypeImpactWarning && (
                  <ConfirmationPopup
                    type="warning"
                    header={t("addNewPlant.waterTypeImpactHeader")}
                    description={t("addNewPlant.waterTypeImpactDescription")}
                    onClose={handleWaterTypeImpactWarningClose}
                    cancelBtn={t("ValueStrings.cancel")}
                    confirmBtn={t("ValueStrings.proceed")}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default AddNewPlant;
